import React, { useState, useEffect, useRef, useCallback } from 'react';
import discountUtils from '../../utils/discount';
import productUtils from '../../utils/product/product-utils';
import config from '../../config/tableConfig';
import Title from '../title-tile/titleTileComponent';

const DiscountForm = ({ onSaved }) => {
  // add title state
  const [discountTitle, setDiscountTitle] = useState('');
  const [type, setType] = useState('BRAND');

  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [success, setSuccess] = useState(false);

  // PRODUCT mode states
  const [products, setProducts] = useState([]);
  const [searchProduct, setSearchProduct] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // BRAND mode states
  const [valuesText, setValuesText] = useState(''); // fallback comma separated for PRODUCT if used
  const [selectedBrands, setSelectedBrands] = useState([]); // array for BRAND
  const [brands, setBrands] = useState([]);
  const [searchBrand, setSearchBrand] = useState('');

  const [percentage, setPercentage] = useState(''); // percentage for the discount
  const [endDate, setEndDate] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // dropdown visibility refs
  const brandContainerRef = useRef(null);
  const productContainerRef = useRef(null);
  const [showBrandDrop, setShowBrandDrop] = useState(false);
  const [showProductDrop, setShowProductDrop] = useState(false);

  useEffect(function () {
    async function loadProductsAndBrands() {
      try {
        var businessConfig = await productUtils.getBusinessConfig();
        if (businessConfig && businessConfig.success) {
          var csv = await productUtils.getCSV(businessConfig.data.productsURL);
          var parsed = productUtils.csvToJSON(csv);

          // set products
          if (parsed && parsed.length) {
            setProducts(parsed);
          } else {
            setProducts([]);
          }

          // compute unique brands (case-insensitive: treat "ACME" and "Acme" as same)
          var unique = [];
          if (Array.isArray(parsed) && parsed.length) {
            var seen = {};
            parsed.forEach(function (item) {
              var raw = item && item['Marca'] ? String(item['Marca']).trim() : '';
              if (!raw) return;
              var key = raw.toLowerCase();
              // preserve first-occurring casing for display
              if (!seen[key]) seen[key] = raw;
            });
            unique = Object.keys(seen).map(function (k) { return seen[k]; });
          }
          unique.sort(function (a, b) {
            return a.toLowerCase().localeCompare(b.toLowerCase());
          });
          setBrands(unique);
        }
      } catch (err) {
        // silent fail
        setProducts([]);
        setBrands([]);
      }
    }
    loadProductsAndBrands();
  }, []);

  // close dropdowns on outside click
  useEffect(function () {
    var onDocClick = function (e) {
      if (brandContainerRef.current && !brandContainerRef.current.contains(e.target)) {
        setShowBrandDrop(false);
      }
      if (productContainerRef.current && !productContainerRef.current.contains(e.target)) {
        setShowProductDrop(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return function () {
      document.removeEventListener('mousedown', onDocClick);
    };
  }, []);

  // ---------- BRAND helpers ----------
  var addBrand = function (brand) {
    if (!brand) return;
    var exists = selectedBrands.indexOf(brand) !== -1;
    if (!exists) {
      setSelectedBrands(selectedBrands.concat(brand));
    }
  };

  var addBrandFromInput = function () {
    var trimmed = (searchBrand || '').trim();
    if (!trimmed) return;
    addBrand(trimmed);
    setSearchBrand('');
    setShowBrandDrop(false);
  };

  var toggleBrand = function (brand) {
    var exists = selectedBrands.indexOf(brand) !== -1;
    if (exists) {
      setSelectedBrands(selectedBrands.filter(function (b) { return b !== brand; }));
    } else {
      setSelectedBrands(selectedBrands.concat(brand));
    }
  };

  var inputSearchBrand = function (e) {
    var b = e.target.value;
    setSearchBrand(b);
    setShowBrandDrop(true);
  };

  var filteredBrands = (brands || []).filter(function (b) {
    if (!searchBrand) return true;
    try {
      return b.toLowerCase().indexOf(searchBrand.toLowerCase()) !== -1;
    } catch (err) {
      return false;
    }
  });

  // ---------- PRODUCT helpers ----------
  var getProductId = function (product) {
    // try config.keyColumn first, then common fallbacks
    if (config && config.keyColumn && product && product[config.keyColumn]) return product[config.keyColumn];
    if (product && product['Codigo']) return product['Codigo'];
    if (product && product['Id']) return product['Id'];
    if (product && product['SKU']) return product['SKU'];
    if (product && product['sku']) return product['sku'];
    if (product && product['Code']) return product['Code'];
    // fallback to JSON string
    return JSON.stringify(product);
  };

  var getProductLabel = function (product) {
    // prefer name/description fields if available
    if (product && product['Descripcion']) return product['Descripcion'];
    if (product && product['Descripción']) return product['Descripción'];
    if (product && product['Nombre']) return product['Nombre'];
    if (product && product['Name']) return product['Name'];
    // fallback to id
    return getProductId(product);
  };

  // new: build full product description joining all fields with hyphen
  var getProductFullLabel = function (product) {
    if (!product) return '';
    try {
      var keys = Object.keys(product || {});
      if (!keys || keys.length === 0) return '';
      var parts = [];
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        var v = product[k];
        if (v === null || v === undefined) continue;
        // convert objects/arrays to JSON string
        if (typeof v === 'object') {
          try {
            v = JSON.stringify(v);
          } catch (e) {
            v = String(v);
          }
        }
        var s = String(v).trim();
        if (s.length > 0) parts.push(s);
      }
      return parts.join(' - ');
    } catch (err) {
      return getProductLabel(product);
    }
  };

  var addProduct = function (product) {
    if (!product) return;
    var id = getProductId(product);
    // avoid duplicates by id
    var exists = selectedProducts.some(function (p) { return getProductId(p) === id; });
    if (!exists) {
      // keep full product object so we can render full description later
      setSelectedProducts(selectedProducts.concat(product));
    }
  };

  var addProductByInput = function () {
    var q = (searchProduct || '').trim();
    if (!q) return;
    // try to find exact product by id or name
    var found = null;
    for (var i = 0; i < (products || []).length; i++) {
      var p = products[i];
      var id = getProductId(p) || '';
      var label = getProductLabel(p) || '';
      var full = getProductFullLabel(p) || '';
      if (id.toString().toLowerCase() === q.toLowerCase() ||
          label.toString().toLowerCase() === q.toLowerCase() ||
          full.toString().toLowerCase() === q.toLowerCase()) {
        found = p;
        break;
      }
    }
    if (found) {
      addProduct(found);
    } else {
      // If not found, do nothing
    }
    setSearchProduct('');
    setShowProductDrop(false);
  };

  var removeSelectedProduct = function (product) {
    var id = getProductId(product);
    setSelectedProducts(selectedProducts.filter(function (p) { return getProductId(p) !== id; }));
  };

  var inputSearchProduct = function (e) {
    var v = e.target.value;
    setSearchProduct(v);
    setShowProductDrop(true);
  };

  var filteredProducts = (products || []).filter(function (p) {
    if (!searchProduct) return true;
    try {
      var label = getProductFullLabel(p) || '';
      var id = getProductId(p) || '';
      return label.toLowerCase().indexOf(searchProduct.toLowerCase()) !== -1 ||
        id.toString().toLowerCase().indexOf(searchProduct.toLowerCase()) !== -1;
    } catch (err) {
      return false;
    }
  });

  // ---------- validation ----------
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var validateForm = useCallback(function () {
    var errs = [];

    if (!discountTitle || (discountTitle + '').trim() === '') {
      errs.push('Título es requerido');
    }

    if (!type || (type + '').trim() === '') {
      errs.push('Tipo es requerido');
    }

    if (type === 'BRAND') {
      if (!selectedBrands || selectedBrands.length === 0) {
        errs.push('Seleccione al menos una marca');
      }
    } else {
      // PRODUCT
      var hasSelectedProducts = selectedProducts && selectedProducts.length > 0;
      var hasValuesText = valuesText && (valuesText + '').trim() !== '';
      if (!hasSelectedProducts && !hasValuesText) {
        errs.push('Seleccione al menos un producto o ingrese valores separados por coma');
      }
    }

    var pct = parseFloat(percentage);
    if (percentage === '' || isNaN(pct)) {
      errs.push('Porcentaje es requerido');
    } else if (pct < 0 || pct > 100) {
      errs.push('Porcentaje debe estar entre 0 y 100');
    }

    return {
      valid: errs.length === 0,
      errors: errs
    };
  });

  // Generate new discount
  const submit = async function (e) {
    e.preventDefault();

    // validate before sending and show errors only after submit
    var v = validateForm();
    if (!v.valid) {
      setErrors(v.errors);
      setShowErrors(true);
      return;
    }

    // clear any previous errors shown
    setErrors([]);
    setShowErrors(false);
    setLoading(true);

    // prepare lists according to model
    var brandList = [];
    var productList = [];

    if (type === 'BRAND') {
      brandList = (selectedBrands || []).slice();
    } else {
      // map selected product objects to id strings
      productList = (selectedProducts || []).map(function (p) {
        return getProductId(p);
      });
      // fallback to comma text if no selectedProducts
      if ((!productList || productList.length === 0) && valuesText) {
        productList = valuesText.split(',').map(function (v) { return v.trim(); }).filter(function (v) { return v; });
      }
    }

    // model expects a single numeric `value`
    var valueNumber = parseFloat(percentage);
    if (isNaN(valueNumber)) valueNumber = 0;

    var payload = {
      title: discountTitle || null,
      type: type,
      brandList: brandList,
      productList: productList,
      value: valueNumber,
      endDate: endDate || null,
      enabled: !!enabled
    };

    try {
      await discountUtils.createDiscount(payload);
      if (onSaved) onSaved();

      // reset form
      setValuesText('');
      setSelectedBrands([]);
      setSelectedProducts([]);
      setPercentage('');
      setEndDate('');
      setEnabled(true);
      setType('BRAND');
      setSearchBrand('');
      setSearchProduct('');
      setShowBrandDrop(false);
      setShowProductDrop(false);
      setDiscountTitle('');
      setErrors([]);
      setShowErrors(false);
      setSuccess(true);
    } catch (err) {
      setErrors([ (err && err.message) || 'Error saving discount' ]);
      setShowErrors(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-3">
      {/* Discount title */}
      <div className="form-group">
        <label>Título</label>
        <input
          className="form-control"
          value={discountTitle}
          onChange={function (e) { setDiscountTitle(e.target.value); }}
          placeholder="Identificador del descuento"
        />
      </div>

      <div className="form-group">
        <label>Tipo</label>
        <select
          className="form-control"
          value={type}
          onChange={function (e) { setType(e.target.value); }}
        >
          <option value="BRAND">BRAND</option>
          <option value="PRODUCT">PRODUCT</option>
        </select>
      </div>

      {type === 'BRAND' ? (
        <>
          <div className="form-group" ref={brandContainerRef} style={{ position: 'relative' }}>
            <label>Seleccione Marcas</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                className="form-control"
                placeholder="Buscar o seleccionar marca"
                value={searchBrand}
                onChange={inputSearchBrand}
                onFocus={function () { setShowBrandDrop(true); }}
                onKeyDown={function (e) {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBrandFromInput();
                  }
                }}
                style={{ marginRight: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addBrandFromInput}
              >
                Agregar
              </button>
            </div>

            {showBrandDrop && filteredBrands && filteredBrands.length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  zIndex: 1000,
                  left: 0,
                  right: 0,
                  maxHeight: 200,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  marginTop: 6,
                  padding: '4px 0',
                  listStyle: 'none'
                }}
              >
                {filteredBrands.map(function (b) {
                  return (
                    <li
                      key={'brand-' + b}
                      onMouseDown={function (ev) {
                        ev.preventDefault();
                        addBrand(b);
                        setSearchBrand('');
                        setShowBrandDrop(false);
                      }}
                      style={{
                        padding: '6px 10px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {b}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label>Marcas seleccionadas</label>
            <div>
              {selectedBrands && selectedBrands.length > 0 ? (
                selectedBrands.map(function (b) {
                  return (
                    <button
                      type="button"
                      key={'button-' + b}
                      className="btn btn-sm btn-outline-secondary mr-1 mb-1"
                      onClick={function () { toggleBrand(b); }}
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}
                    >
                      {b} &nbsp;×
                    </button>
                  );
                })
              ) : (
                <div className="text-muted">No hay marcas seleccionadas</div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="form-group" ref={productContainerRef} style={{ position: 'relative' }}>
            <label>Seleccione Productos</label>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                className="form-control"
                placeholder="Buscar por código o descripción"
                value={searchProduct}
                onChange={inputSearchProduct}
                onFocus={function () { setShowProductDrop(true); }}
                onKeyDown={function (e) {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addProductByInput();
                  }
                }}
                style={{ marginRight: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addProductByInput}
              >
                Agregar
              </button>
            </div>

            {showProductDrop && filteredProducts && filteredProducts.length > 0 && (
              <ul
                style={{
                  position: 'absolute',
                  zIndex: 1000,
                  left: 0,
                  right: 0,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  marginTop: 6,
                  padding: '4px 0',
                  listStyle: 'none'
                }}
              >
                {filteredProducts.map(function (p) {
                  var id = getProductId(p);
                  var full = getProductFullLabel(p);
                  return (
                    <li
                      key={'prod-' + id}
                      onMouseDown={function (ev) {
                        ev.preventDefault();
                        addProduct(p);
                        setSearchProduct('');
                        setShowProductDrop(false);
                      }}
                      style={{
                        padding: '8px 10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f1f1',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                      title={full}
                    >
                      {full}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label>Productos seleccionados</label>
            <div>
              {selectedProducts && selectedProducts.length > 0 ? (
                selectedProducts.map(function (p) {
                  var id = getProductId(p);
                  var full = getProductFullLabel(p);
                  return (
                    <div
                      key={'selprod-' + id}
                      style={{ display: 'inline-flex', alignItems: 'center', marginRight: 8, marginBottom: 8 }}
                    >
                      <div
                        className="btn btn-sm btn-outline-secondary"
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 420
                        }}
                        title={full}
                      >
                        {full}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger ml-1"
                        onClick={function () { removeSelectedProduct(p); }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="text-muted">No hay productos seleccionados</div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="form-group">
        <label>Porcentaje (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          step="0.1"
          className="form-control"
          value={percentage}
          onChange={function (e) { setPercentage(e.target.value); }}
          placeholder="Ej. 10 para 10%"
        />
      </div>

      <div className="form-group">
        <label>Fecha fin Descuento</label>
        <input
          type="date"
          className="form-control"
          value={endDate}
          onChange={function (e) { setEndDate(e.target.value); }}
        />
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={function (e) { setEnabled(e.target.checked); }}
          />{' '}
          Habilitado
        </label>
      </div>

      {/* show validation errors only after pressing Guardar */}
      {showErrors && errors && errors.length > 0 && (
        <div className="alert alert-danger">
          <ul className="mb-0">
            {errors.map(function (err, idx) {
              return <li key={'err-' + idx}>{err}</li>;
            })}
          </ul>
        </div>
      )}

      <button className="btn btn-dark btn-block" disabled={loading}>
        {loading ? 'Guardando...' : 'Guardar'}
      </button>

       <div
            className="col-12 success-signup"
            style={{ display: success ? 'block' : 'none' }}
          >
            <Title
              message="¡El descuento se creó correctamente!"
              theme="success"
            />
            <a
              className="btn btn-dark btn-cart"
              href="/discounts"
              role="button"
            >
              Volver
            </a>
          </div>
    </form>
  );
};

export default DiscountForm;
